import type { SimulatorProduct, SimulatorRequest, SimulatorResult } from "common/types/Simulator";
import { money, percent } from "../format";
import { allocationLabels, creditTypeLabels, deferTypeLabels, ownershipLabels, taxRegimeLabels } from "../labels";
import { exposureNote, feesNote, projectionDisclaimer } from "../texts";

/** Valeur d'un export : les nombres restent des nombres pour Excel ; Word les met en forme comme l'écran. */
export type ReportCell = { type: "text"; value: string } | { type: "money"; value: number | null } | { type: "percent"; value: number | null } | { type: "integer"; value: number };

export type ReportItem = { label: string; value: ReportCell; note?: string };
export type ReportTable = { columns: string[]; rows: ReportCell[][] };

export type SimulationReport = {
	title: string;
	summary: string;
	generatedLabel: string;
	fileName: string;
	hypotheses: ReportItem[];
	keyFigures: ReportItem[];
	/** Colonnes du tableau annuel de l'écran. */
	annual: ReportTable;
	/** Toutes les colonnes calculées, pour le tableur. */
	annualDetail: ReportTable;
	allocation: ReportTable;
	fees: ReportTable;
	feeTotals: ReportItem[];
	sources: ReportTable;
	geography: ReportTable;
	sectors: ReportTable;
	warnings: string[];
	notes: string[];
};

/** Le résultat affiché et les paramètres qui l'ont produit. */
export type SimulationExportInput = { request: SimulatorRequest; result: SimulatorResult; products: SimulatorProduct[] };

export const NOT_PROVIDED = "Non renseigné";

const text = (value: string): ReportCell => ({ type: "text", value });
const amount = (value: number | null): ReportCell => ({ type: "money", value });
const rate = (value: number | null): ReportCell => ({ type: "percent", value });
const integer = (value: number): ReportCell => ({ type: "integer", value });
const duration = (years: number) => `${years} an${years > 1 ? "s" : ""}`;
const yesNo = (value: boolean) => text(value ? "Oui" : "Non");
const listOrNone = (values: string[]) => text(values.length ? values.join(", ") : "Aucune");

export function buildSimulationReport({ request, result, products }: SimulationExportInput, generatedAt = new Date()): SimulationReport {
	const first = result.years[0]!;
	const last = result.years[result.years.length - 1]!;
	const parisDay = new Intl.DateTimeFormat("fr-CA", { timeZone: "Europe/Paris" }).format(generatedAt);
	const generated = new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeStyle: "short", timeZone: "Europe/Paris" }).format(generatedAt);
	const { credit, tax } = request;

	const hypotheses: ReportItem[] = [
		{ label: "Montant à investir", value: amount(request.budget) },
		{ label: "Répartition", value: text(allocationLabels[request.allocation]) },
		{ label: "Mode de détention", value: text(ownershipLabels[request.ownership]) },
		...(request.ownership === "full" ? [] : [{ label: "Durée du démembrement", value: text(duration(request.ownershipYears)) }]),
		{ label: "Horizon", value: text(duration(request.years)) },
		{ label: "Baisse des dividendes / an", value: rate(request.dividendDecrease) },
		{ label: "Évolution du prix / an", value: rate(request.appreciation) },
		{ label: "Crédit", value: yesNo(credit.enabled) },
		...(credit.enabled
			? [
					{ label: "Type de crédit", value: text(creditTypeLabels[credit.type]) },
					{ label: "Montant emprunté", value: amount(credit.amount) },
					{ label: "Taux nominal annuel", value: rate(credit.rate) },
					{ label: "Durée du crédit, différé inclus", value: text(duration(credit.years)) },
					{ label: "Assurance annuelle sur capital initial", value: rate(credit.insurance) },
					{ label: "Différé", value: text(credit.deferMonths > 0 ? `${credit.deferMonths} mois` : "Aucun") },
					...(credit.deferMonths > 0 ? [{ label: "Type de différé", value: text(deferTypeLabels[credit.deferType]) }] : []),
				]
			: []),
		{ label: "Fiscalité estimée", value: yesNo(tax.enabled) },
		...(tax.enabled
			? [
					{ label: "Régime fiscal", value: text(taxRegimeLabels[tax.regime]) },
					{ label: "Tranche marginale d’imposition", value: rate(tax.marginalRate) },
					{ label: "Prélèvements sociaux", value: rate(tax.socialRate) },
					{ label: "Part des revenus étrangers", value: rate(tax.foreignShare) },
					{ label: "Taux effectif sur revenus étrangers", value: rate(tax.foreignRate) },
					{ label: "Supplément d’IFI estimé", value: yesNo(tax.ifi) },
					...(tax.ifi ? [{ label: "Autre patrimoine immobilier net taxable", value: amount(tax.otherWealth) }] : []),
				]
			: []),
		{ label: "Pays exclus", value: listOrNone(request.excludedCountries) },
		{ label: "Secteurs exclus", value: listOrNone(request.excludedSectors) },
	];

	const keyFigures: ReportItem[] = [
		{ label: "Investissement", value: amount(result.invested), note: `${money(result.remaining)} disponibles` },
		{ label: "Apport initial", value: amount(result.contribution) },
		{ label: "Revenus · année 1", value: amount(first.income), note: `${money(first.income / 12)} / mois en moyenne` },
		{
			label: first.cashFlow < 0 ? "Effort mensuel · an 1" : "Excédent mensuel · an 1",
			value: amount(Math.abs(first.cashFlow) / 12),
			note: tax.enabled ? "Après crédit et fiscalité" : "Après crédit, avant fiscalité",
		},
		{ label: "TRI annualisé", value: result.irr === null ? text("Non calculable") : rate(result.irr), note: "Valeur finale et dette incluses" },
		{
			label: "Revenus annuels sans délai de jouissance",
			value: amount(result.annualRunRate),
			...(result.invested > 0 ? { note: `${percent(result.annualRunRate / result.invested)} du montant investi` } : {}),
		},
		{ label: "Revenus nets de première année", value: amount(first.netIncome) },
		{ label: `Valeur de sortie estimée · année ${last.year}`, value: amount(last.asset) },
		{ label: "Dette restante", value: amount(last.debt) },
		{ label: "Capital net à terme", value: amount(last.asset - last.debt) },
	];

	const fees = result.lines.flatMap((line) => {
		const amounts = [line.fees.subscription.amount, line.fees.ongoing.amount, line.fees.transaction.amount];
		const total = amounts.some((value) => value === null) ? null : amounts.reduce<number>((sum, value) => sum + value!, 0);
		const subscriptionRate = line.fees.subscription.rate;
		return [
			// Le taux de souscription de la fiche est en points ; les frais courants et de transaction sont des fractions.
			[text(line.name), text("Souscription, inclus dans le prix"), rate(subscriptionRate === null ? null : subscriptionRate / 100), amount(line.fees.subscription.amount)],
			[text(line.name), text("Courants, par an"), rate(line.fees.ongoing.rate), amount(line.fees.ongoing.amount)],
			[text(line.name), text("Transaction, par an"), rate(line.fees.transaction.rate), amount(line.fees.transaction.amount)],
			[text(line.name), text("Total indicatif"), text(""), amount(total)],
		];
	});

	const ownership = ownershipLabels[request.ownership];
	const options = [ownership, ...(credit.enabled ? ["À crédit"] : []), ...(tax.enabled ? ["Fiscalité estimée"] : [])];
	const selectedCount = result.lines.length;

	return {
		title: "Simulation SCPI",
		summary: `${selectedCount} SCPI sélectionnée${selectedCount > 1 ? "s" : ""} · Projection sur ${duration(result.years.length)} · ${options.join(" · ")}`,
		generatedLabel: `Éditée le ${generated}`,
		fileName: `Simulation SCPI Wenimmo ${parisDay}`,
		hypotheses,
		keyFigures,
		annual: {
			columns: ["Année", "Revenus", "Impôts + IFI", "Crédit + assurance", "Flux net", "Dette restante", "Valeur de sortie"],
			rows: result.years.map((y) => [integer(y.year), amount(y.income), amount(y.tax + y.ifi), amount(y.debtPayments), amount(y.cashFlow), amount(y.debt), amount(y.asset)]),
		},
		annualDetail: {
			columns: [
				"Année",
				"Revenus",
				"Impôts",
				"IFI",
				"Revenus nets",
				"Crédit + assurance",
				"Dont intérêts",
				"Dont assurance",
				"Flux net",
				"Flux cumulés, apport inclus",
				"Dette restante",
				"Valeur de sortie",
				"Capital net",
			],
			rows: result.years.map((y) => [
				integer(y.year),
				amount(y.income),
				amount(y.tax),
				amount(y.ifi),
				amount(y.netIncome),
				amount(y.debtPayments),
				amount(y.interest),
				amount(y.insurance),
				amount(y.cashFlow),
				amount(y.cumulativeCashFlow),
				amount(y.debt),
				amount(y.asset),
				amount(y.asset - y.debt),
			]),
		},
		allocation: {
			columns: ["SCPI", "Parts", "Prix du droit", "Investissement", "Taux retenu", "Base du taux", "Revenus année 1"],
			rows: result.lines.map((line) => [
				text(line.name),
				integer(line.shares),
				amount(line.unitCost),
				amount(line.invested),
				rate(line.distributionRate / 100),
				text(line.distributionRateSource === "net" ? "Net" : "Brut"),
				amount(line.firstYearIncome),
			]),
		},
		fees: { columns: ["SCPI", "Frais", "Taux", "Montant sur la durée"], rows: fees },
		feeTotals: [
			{ label: "Souscription", value: amount(result.fees.subscription) },
			{ label: "Courants", value: amount(result.fees.ongoing) },
			{ label: "Transaction", value: amount(result.fees.transaction) },
			{ label: "Total indicatif", value: amount(result.fees.total) },
		],
		sources: {
			columns: ["SCPI", "Valeur de reconstitution", "Date de la source"],
			rows: result.lines.map((line) => {
				const data = products.find((p) => p.productId === line.productId)?.simulatorData;
				return [text(line.name), data?.reconstitutionValue == null ? text("Non renseignée") : amount(data.reconstitutionValue), text(data?.sourceDate ?? "Non renseignée")];
			}),
		},
		geography: { columns: ["Pays", "Part"], rows: result.geography.map((e) => [text(e.label), rate(e.weight)]) },
		sectors: { columns: ["Secteur", "Part"], rows: result.sectors.map((e) => [text(e.label), rate(e.weight)]) },
		warnings: result.warnings,
		notes: [projectionDisclaimer, feesNote(result.fees.subscription === null ? NOT_PROVIDED : money(result.fees.subscription), result.years.length), exposureNote],
	};
}

/** Mise en forme française d'une valeur, identique à l'écran. */
export function formatCell(cell: ReportCell): string {
	if (cell.type === "text") return cell.value;
	if (cell.type === "integer") return String(cell.value);
	if (cell.value === null) return NOT_PROVIDED;
	return cell.type === "money" ? money(cell.value) : percent(cell.value);
}
